/**
 * Authored environment settings stored on the map. Time of day and wind are the only live
 * channels: wind is a constant per-turn drift that offsets the movement disk, and time of day
 * grades the sky. Clouds, rain, chop, and fog were removed with their renderers and will be
 * rebuilt as their own systems later; the editor keeps disabled placeholders for them.
 */

import { sunSettingsFromTimeOfDay } from './sun.ts';

export type WindSettings = {
  /** Off leaves every map at exactly its authored feel; on offsets the movement disk. */
  enabled: boolean;
  /** Compass degrees the wind blows *towards* (0 = north). */
  directionDeg: number;
  /** Metres per second. Coupling to disk drift is `windOffsetPx` (squared, capped). */
  speed: number;
};

/** Geometric sun. Independent of `timeOfDay`, which only grades the sky. */
export type SunSettings = {
  /** 0 = dawn (east), 1 = sunset (west). */
  azimuth: number;
  /** 0 = horizon, 1 = zenith. */
  altitude: number;
  /** 0–1 irradiance scale. */
  strength: number;
};

export const DEFAULT_SUN: SunSettings = {
  azimuth: 0.5,
  altitude: 1,
  strength: 1,
};

export type Environment = {
  /** Deterministic seed for terrain generation (terraces, decoration scatter). Not weather. */
  seed: number;
  /** When false, the scene is noon; when true, `timeOfDay` grades the sky. */
  timeEnabled: boolean;
  /** 0 is midnight, 0.5 is noon. Static for the whole race. Grades the sky, not the sun disc. */
  timeOfDay: number;
  sun: SunSettings;
  wind: WindSettings;
};

/** Editor spinbutton ceiling. Play uses the stored map value. */
export const MAX_WIND_SPEED = 100;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export type EnvironmentPatch = {
  seed?: number;
  timeEnabled?: boolean;
  timeOfDay?: number;
  sun?: Partial<SunSettings>;
  wind?: Partial<WindSettings>;
};

/**
 * Applies an authored change, clamping each field to what the renderer can express. Turning Time
 * off snaps the scene back to noon (sky and sun disc) so "off" always means the same thing, and a
 * later turn-on defaults to that noon until a preset is picked.
 */
export function updateEnvironment(environment: Environment, patch: EnvironmentPatch): void {
  if (patch.seed !== undefined) {
    environment.seed = Math.max(0, Math.round(patch.seed));
  }

  if (patch.timeEnabled !== undefined) {
    environment.timeEnabled = patch.timeEnabled;

    if (!patch.timeEnabled) {
      environment.timeOfDay = 0.5;
      environment.sun = sunSettingsFromTimeOfDay(0.5);
    }
  }

  if (patch.timeOfDay !== undefined) {
    environment.timeOfDay = clamp01(patch.timeOfDay);
  }

  if (patch.sun) {
    environment.sun = {
      azimuth: clamp01(patch.sun.azimuth ?? environment.sun.azimuth),
      altitude: clamp01(patch.sun.altitude ?? environment.sun.altitude),
      strength: clamp01(patch.sun.strength ?? environment.sun.strength),
    };
  }

  if (patch.wind) {
    const wind = { ...environment.wind, ...patch.wind };

    environment.wind = {
      enabled: wind.enabled,
      directionDeg: ((wind.directionDeg % 360) + 360) % 360,
      speed: Math.min(MAX_WIND_SPEED, Math.max(0, wind.speed)),
    };
  }
}

/** Display-only: `timeOfDay` is a fraction of a day, but authors think in hours. */
export function timeOfDayToHours(timeOfDay: number): number {
  return clamp01(timeOfDay) * 24;
}

export function hoursToTimeOfDay(hours: number): number {
  return clamp01((((hours % 24) + 24) % 24) / 24);
}

export type TimeOfDayPreset = 'night' | 'morning' | 'day' | 'evening';

/**
 * The four times of day the editor offers. Morning and evening sit inside the 06:00–18:00
 * window so the sun still has a ground direction (long water-plane shade) instead of sitting
 * on the horizon, where irradiance is faded to 0.
 */
export const TIME_OF_DAY_PRESETS: ReadonlyArray<{
  id: TimeOfDayPreset;
  label: string;
  timeOfDay: number;
}> = [
  { id: 'night', label: 'Night', timeOfDay: hoursToTimeOfDay(0) },
  { id: 'morning', label: 'Morning', timeOfDay: hoursToTimeOfDay(8) },
  { id: 'day', label: 'Day', timeOfDay: hoursToTimeOfDay(12) },
  { id: 'evening', label: 'Evening', timeOfDay: hoursToTimeOfDay(16) },
];

export function presetToTimeOfDay(id: TimeOfDayPreset): number {
  const preset = TIME_OF_DAY_PRESETS.find((candidate) => candidate.id === id);
  return preset ? preset.timeOfDay : hoursToTimeOfDay(12);
}

/** Time dropdown patch: sky clock plus a matching default disc. Sliders may diverge after. */
export function timePresetPatch(
  id: TimeOfDayPreset,
): Pick<EnvironmentPatch, 'timeOfDay' | 'sun'> {
  const timeOfDay = presetToTimeOfDay(id);
  return { timeOfDay, sun: sunSettingsFromTimeOfDay(timeOfDay) };
}

/**
 * Picks the preset whose hour is nearest the stored `timeOfDay`, measured around the clock so an
 * hour just before midnight still reads as Night. Keeps the select in sync with a hand-authored or
 * migrated value that does not land exactly on a preset.
 */
export function timeOfDayToPreset(timeOfDay: number): TimeOfDayPreset {
  const target = clamp01(timeOfDay);
  let best: TimeOfDayPreset = 'day';
  let bestDistance = Infinity;

  for (const preset of TIME_OF_DAY_PRESETS) {
    const raw = Math.abs(target - preset.timeOfDay);
    const distance = Math.min(raw, 1 - raw);

    if (distance < bestDistance) {
      bestDistance = distance;
      best = preset.id;
    }
  }

  return best;
}

/** Calm, clear, noon: wind and time both off until an author turns them on. */
export function createDefaultEnvironment(): Environment {
  return {
    seed: 1,
    timeEnabled: false,
    timeOfDay: 0.5,
    sun: { ...DEFAULT_SUN },
    wind: {
      enabled: false,
      directionDeg: 0,
      speed: 0,
    },
  };
}
